@if (error(); as messageKey) {
  <p class="runner-pairing__result runner-pairing__result--error" role="alert">
    <mat-icon aria-hidden="true">error_outline</mat-icon>
    {{ messageKey | translate }}
  </p>
}

<ul class="runner-pairing__list">
  @for (runner of connected(); track runner.id) {
    @let state = runnerState(runner);
    <li
      class="runner-pairing__item"
      data-testid="runner-row"
      [attr.data-runner-id]="runner.id"
      [class.runner-pairing__item--disconnected]="state === 'disconnected'"
    >
      @if (showDetails()) {
        <span
          class="runner-pairing__dot"
          aria-hidden="true"
          [class.runner-pairing__dot--online]="state === 'online'"
          [class.runner-pairing__dot--working]="state === 'working'"
          [class.runner-pairing__dot--offline]="state === 'offline' || state === 'disconnected'"
        ></span>
      } @else {
        <span class="runner-pairing__dot" aria-hidden="true" [class.runner-pairing__dot--online]="runner.online"></span>
      }
      <span class="runner-pairing__name">{{ runner.name }}</span>
      @if (runner.preferred) {
        <span class="runner-pairing__badge">{{ 'runnerPairing.connectedRunners.preferred' | translate }}</span>
      }
      @if (showDetails()) {
        <span class="runner-pairing__seen" data-testid="runner-state">
          @switch (state) {
            @case ('disconnected') {
              {{ 'runners.status.disconnected' | translate }}
            }
            @case ('online') {
              {{ 'runners.status.online' | translate }}
            }
            @case ('working') {
              {{ 'runners.status.working' | translate }}
            }
            @case ('offline') {
              {{
                runner.lastSeenAt
                  ? ('runners.status.offlineSeen' | translate: { when: runner.lastSeenAt | date: 'short' })
                  : ('runners.status.offline' | translate)
              }}
              @if (runner.queuedCount > 0) {
                <span class="runner-pairing__waiting">
                  {{
                    runner.queuedCount === 1
                      ? ('runners.status.waitingOne' | translate)
                      : ('runners.status.waitingMany' | translate: { count: runner.queuedCount })
                  }}
                </span>
              }
            }
          }
        </span>
      } @else {
        <span class="runner-pairing__seen">
          {{
            runner.online
              ? ('runnerPairing.connectedRunners.online' | translate)
              : ('runnerPairing.connectedRunners.lastSeen' | translate: { when: runner.lastSeenAt | date: 'short' })
          }}
        </span>
      }
      @if (showDetails() && runner.canRename) {
        <button
          type="button"
          class="runner-pairing__rename"
          data-testid="runner-rename"
          [attr.aria-label]="'runners.rename.actionAria' | translate: { name: runner.name }"
          (click)="pendingRename.set(runner)"
        >
          {{ 'runners.rename.action' | translate }}
        </button>
      }
      @if (showDetails() && runner.canRemove) {
        <button
          type="button"
          class="runner-pairing__remove"
          data-testid="runner-remove"
          [disabled]="removing()"
          [attr.aria-label]="'runners.remove.actionAria' | translate: { name: runner.name }"
          (click)="askRemove(runner)"
        >
          {{ 'runners.remove.action' | translate }}
        </button>
      }
      @if (runner.canDisconnect) {
        <button
          type="button"
          class="runner-pairing__disconnect"
          [disabled]="disconnecting() !== null"
          [attr.aria-label]="'runnerPairing.connectedRunners.disconnectAria' | translate: { name: runner.name }"
          (click)="askDisconnect(runner)"
        >
          {{ 'runnerPairing.connectedRunners.disconnect' | translate }}
        </button>
      } @else if (!runner.canRename && !runner.canRemove) {
        <span class="runner-pairing__foreign">
          {{ 'runnerPairing.connectedRunners.foreign' | translate }}
        </span>
      }
      @if (showDetails()) {
        @let engineKey = 'runners.engine.' + runner.engine;
        @let engineText = engineKey | translate;
        <dl class="runner-pairing__details">
          <div class="runner-pairing__detail">
            <dt>{{ 'runners.details.engine' | translate }}</dt>
            <dd data-testid="runner-engine">{{ engineText === engineKey ? runner.engine : engineText }}</dd>
          </div>
          <div class="runner-pairing__detail runner-pairing__detail--version">
            <dt>{{ 'runners.details.version' | translate }}</dt>
            <dd data-testid="runner-version">{{ runner.version ?? ('runners.version.unknown' | translate) }}</dd>
            @if (updatePill(runner); as hint) {
              <dd class="runner-pairing__update">
                <span
                  class="runner-pairing__update-anchor"
                  data-testid="runner-update"
                  tabindex="0"
                  [matTooltip]="
                    'runners.update.' + hint.key + '.tooltip' | translate: { version: runner.latestVersion }
                  "
                >
                  <app-res-pill
                    size="sm"
                    [status]="hint.status"
                    [label]="'runners.update.' + hint.key + '.label' | translate"
                  />
                </span>
              </dd>
            }
          </div>
          <div class="runner-pairing__detail runner-pairing__detail--job">
            <dt>{{ 'runners.details.job' | translate }}</dt>
            <dd data-testid="runner-job">
              @if (runner.currentJob; as job) {
                @if (job.foreign) {
                  <span data-testid="runner-job-foreign">{{ 'runners.job.foreign' | translate }}</span>
                } @else if (job.projectId && job.testRunId) {
                  <a
                    class="runner-pairing__job-link"
                    data-testid="runner-job-link"
                    [routerLink]="['/projects', job.projectId, 'test-runs', job.testRunId]"
                  >
                    {{ jobLabel(job) }}
                  </a>
                } @else {
                  <span>{{ job.testCaseTitle || ('runners.job.running' | translate) }}</span>
                }
                <app-res-pill
                  data-testid="runner-job-status"
                  size="sm"
                  status="running"
                  [pulsing]="true"
                  [label]="
                    (job.cancelRequested ? 'testCases.run.cancel.requested' : 'testCases.run.jobStatus.RUNNING')
                      | translate
                  "
                />
              }
            </dd>
          </div>
          <div class="runner-pairing__detail">
            <dt>{{ 'runners.details.queued' | translate }}</dt>
            <dd data-testid="runner-queued">
              @if (runner.queuedCount > 0) {
                {{ 'runners.queued' | translate: { count: runner.queuedCount } }}
              }
            </dd>
          </div>
        </dl>
      }
      @if (runner.reportedLabels.length > 0 || runner.managedLabels.length > 0 || runner.canEditLabels) {
        <div class="runner-pairing__tags">
          @for (label of runner.reportedLabels; track label) {
            <span
              class="runner-pairing__tag runner-pairing__tag--reported"
              [attr.title]="'runnerPairing.labels.reportedHint' | translate"
            >
              <span class="runner-pairing__tag-text">{{ label }}</span>
              <span class="sr-only">{{ 'runnerPairing.labels.reportedHint' | translate }}</span>
            </span>
          }
          @for (label of runner.managedLabels; track label) {
            <span class="runner-pairing__tag runner-pairing__tag--managed">
              <span class="runner-pairing__tag-text">{{ label }}</span>
              @if (runner.canEditLabels) {
                <button
                  type="button"
                  class="runner-pairing__tag-remove"
                  [disabled]="savingLabels() !== null"
                  [attr.aria-label]="'runnerPairing.labels.removeAria' | translate: { label }"
                  (click)="removeLabel(runner, label)"
                >
                  <mat-icon aria-hidden="true">close</mat-icon>
                </button>
              }
            </span>
          }
          @if (runner.canEditLabels) {
            <span class="runner-pairing__tag-add">
              <input
                type="text"
                class="runner-pairing__tag-input"
                data-testid="runner-label-input"
                autocomplete="off"
                maxlength="64"
                [attr.data-runner-id]="runner.id"
                [attr.aria-describedby]="'runner-label-hint-' + runner.id"
                [attr.aria-label]="'runnerPairing.labels.inputAria' | translate: { name: runner.name }"
                [placeholder]="'runnerPairing.labels.placeholder' | translate"
                [value]="labelDraft(runner.id)"
                [disabled]="savingLabels() === runner.id"
                (input)="setLabelDraft(runner.id, $event)"
                (keydown.enter)="addLabel(runner)"
              />
              <button
                type="button"
                class="runner-pairing__tag-add-btn"
                data-testid="runner-label-add"
                [disabled]="!canAddLabel(runner)"
                (click)="addLabel(runner)"
              >
                @if (savingLabels() === runner.id) {
                  <mat-progress-spinner mode="indeterminate" diameter="14" />
                }
                {{ 'runnerPairing.labels.add' | translate }}
              </button>
            </span>
          }
        </div>
      }
      @if (runner.canEditLabels) {
        <p class="runner-pairing__tag-hint" aria-live="polite" [id]="'runner-label-hint-' + runner.id">
          @if (labelProblem(runner); as problem) {
            <span data-testid="runner-label-invalid">{{ 'runnerPairing.labels.' + problem | translate }}</span>
          }
        </p>
      }
      @if (labelError() === runner.id) {
        <p class="runner-pairing__tag-error" role="alert" data-testid="runner-label-error">
          <mat-icon aria-hidden="true">error_outline</mat-icon>
          {{ 'runnerPairing.labels.saveFailed' | translate }}
        </p>
      }
    </li>
  }
</ul>

@let queuedKey = disconnectQueuedKey();
<app-confirm-dialog
  [visible]="pendingDisconnect() !== null"
  [title]="'runnerPairing.connectedRunners.confirmTitle' | translate"
  [message]="
    ('runnerPairing.connectedRunners.confirmMessage' | translate: { name: pendingDisconnect()?.name }) +
    (queuedKey ? ' ' + (queuedKey | translate: { count: pendingDisconnect()?.queuedCount }) : '')
  "
  [confirmLabel]="'runnerPairing.connectedRunners.confirmOk' | translate"
  [cancelLabel]="'runnerPairing.connectedRunners.confirmCancel' | translate"
  [busy]="disconnecting() !== null"
  (confirmed)="confirmDisconnect()"
  (cancelled)="pendingDisconnect.set(null)"
/>

<app-confirm-dialog
  icon="delete"
  [visible]="pendingRemove() !== null"
  [title]="'runners.remove.title' | translate"
  [message]="'runners.remove.message' | translate: { name: pendingRemove()?.name }"
  [confirmLabel]="'runners.remove.confirm' | translate"
  [cancelLabel]="'runners.remove.cancel' | translate"
  [busy]="removing()"
  (confirmed)="confirmRemove()"
  (cancelled)="cancelRemove()"
>
  @if (removeError(); as messageKey) {
    <p class="runner-pairing__result runner-pairing__result--error" role="alert" data-testid="runner-remove-error">
      {{ messageKey | translate }}
    </p>
  }
</app-confirm-dialog>

@if (pendingRename(); as runner) {
  <app-runner-rename-dialog [runner]="runner" (renamed)="onRenamed($event)" (cancelled)="pendingRename.set(null)" />
}
